// Terms of Service — plain-English version. Written from scratch to match
// how JoP actually operates. Covers purchases, commissions, keepsakes, events,
// and the usual IP/liability/disputes boilerplate every small shop needs.
//
// Lawyer review recommended before final publication.

import PolicyPage from "./PolicyPage";

export default function TermsOfService() {
  return (
    <PolicyPage title="Terms of Service" lastUpdated="April 16, 2026">
      <p>
        Welcome. These Terms of Service ("Terms") govern your use of the website <a href="https://thejoyofpancakes.com">thejoyofpancakes.com</a> and any services we offer, including pancake art performances, live-event bookings, classes, commissions, merch, keepsake pages, and livestream participation. The service is operated by <strong>Lucky Bean LLC</strong> ("we," "us," "our"), based in St. Louis, Missouri.
      </p>

      <p>
        By using the site, placing an order, or booking us for an event, you agree to these Terms. If you don't, please don't use the service.
      </p>

      <h2>Who can use the service</h2>
      <p>
        You need to be at least 18 to make a purchase or book an event. If you're under 18, that's OK — just have a parent or legal guardian handle the order. You agree to provide accurate contact and billing information.
      </p>

      <h2>Orders, pricing, and payment</h2>
      <ul>
        <li>All prices are in US dollars. Payment is processed by Square. We don't store your card details.</li>
        <li>We may refuse or cancel any order, including after confirmation. If we do, we'll refund you in full.</li>
        <li>Typos happen. If an item is listed at an obviously incorrect price, we may cancel the order and issue a refund before fulfillment.</li>
        <li>Applicable sales tax is collected where required.</li>
      </ul>

      <h2>Bookings and commissions</h2>

      <h3>Live events</h3>
      <p>
        Booking us for a live performance, pop-up, or class requires a signed agreement and a deposit. Details are covered in your individual contract, which takes precedence over these Terms in case of conflict. A booking isn't confirmed until the deposit is received.
      </p>

      <h3>Custom pancake art (preserved gifts, pickup commissions, cake toppers, bulk runs)</h3>
      <p>
        Commissions are custom work and <strong>non-refundable</strong> once production starts. We'll share concept or progress previews so you can flag concerns early. Delivery timelines are estimates unless stated otherwise in your order.
      </p>

      <h3>Classes and 1-on-1 lessons</h3>
      <p>
        Refund windows are spelled out in our <a href="/shipping">Shipping & Returns</a> policy. In short: public classes are refundable up to 48 hours before the session; 1-on-1 lessons are refundable up to 7 days before. We can usually reschedule instead — ask.
      </p>

      <h2>Shipping and returns</h2>
      <p>
        Covered in our <a href="/shipping">Shipping & Returns</a> policy.
      </p>

      <h2>Keepsake pages and livestream participation</h2>
      <p>
        When we make a pancake for you, we create a personal keepsake page accessible via the QR code on your ticket. That page may include:
      </p>
      <ul>
        <li>A photo of your pancake.</li>
        <li>Your first name or the name you gave us at ordering (you can request we remove or change this).</li>
        <li>An optional description we generated to make the art accessible to screen readers.</li>
      </ul>
      <p>
        By placing an order (in person, at a livestream, or online) you grant us a non-exclusive, royalty-free license to photograph, display, and share images of the finished pancake — including on our website, in social media posts, in our portfolio, and in marketing materials. We will never associate your <em>full name</em> with a public gallery appearance without your permission.
      </p>
      <p>
        You can ask us to remove a keepsake or take a pancake image off our public channels at any time by emailing <a href="mailto:hello@thejoyofpancakes.com">hello@thejoyofpancakes.com</a>. We'll comply within a reasonable time.
      </p>

      <h3>Livestream participants</h3>
      <p>
        When you join a livestream queue (via the "Join" link), you're agreeing to have your requested design, name, and pancake art appear on the stream. If you submit a reference photo (e.g., for a custom piece), you confirm that you have the rights to share it with us and that it doesn't violate anyone else's rights.
      </p>

      <h2>Testimonials and public reviews</h2>
      <p>
        If you submit a review from the keepsake page, you grant us the right to publish it (with the name and context you provided) on our website and marketing. We may edit reviews for length or clarity without changing the meaning, and we may choose not to publish a review at our discretion.
      </p>

      <h2>Intellectual property</h2>

      <h3>Our work</h3>
      <p>
        All site content — logos, artwork, photos of pancakes, writing, software, page layouts, characters, designs — is owned by Lucky Bean LLC or used with permission. You may share photos of your own pancake freely. You may not reuse our logos, character designs, flash designs, or other branded material for your own business purposes without written permission.
      </p>

      <h3>Your content</h3>
      <p>
        When you submit something to us (reference photos, testimonials, notes on your pancake), you retain ownership of it. You grant us a non-exclusive license to use it in the ways described above. You're responsible for making sure you have the rights to what you submit.
      </p>

      <h3>Reference photos and likeness</h3>
      <p>
        If you submit a reference photo of a person, you confirm that you have their permission. We reserve the right to decline requests that include protected characters or logos without authorization, or that are otherwise inappropriate.
      </p>

      <h2>Acceptable use</h2>
      <p>
        Please don't:
      </p>
      <ul>
        <li>Submit content that is hateful, harassing, obscene, defamatory, or promotes violence.</li>
        <li>Attempt to bypass security measures, scrape the site programmatically, or interfere with other users.</li>
        <li>Impersonate someone else.</li>
      </ul>
      <p>
        We may refuse service or remove content that violates these rules.
      </p>

      <h2>Disclaimers</h2>
      <p>
        <strong>The site and services are provided "as is."</strong> We make no warranties about uptime, completeness, or fitness for any particular purpose. Pancakes are food; if you have dietary restrictions or allergies, please tell us before we start cooking. Our standard batter contains wheat, dairy, and egg. We cannot guarantee a kitchen or griddle free of common allergens.
      </p>

      <h2>Limitation of liability</h2>
      <p>
        To the fullest extent allowed by law, our total liability for any claim arising from your use of the service is limited to the amount you paid us in the 12 months preceding the claim. We're not liable for indirect, incidental, consequential, or punitive damages. Some jurisdictions don't allow these limitations, in which case they apply to the extent permitted.
      </p>

      <h2>Indemnification</h2>
      <p>
        You agree to defend and hold us harmless from any claims resulting from your violation of these Terms, your misuse of the service, or content you submit that infringes a third party's rights.
      </p>

      <h2>Governing law and disputes</h2>
      <p>
        These Terms are governed by the laws of the State of Missouri, without regard to conflict-of-laws principles. Any dispute will be resolved in the state or federal courts located in St. Louis County, Missouri, and you consent to their jurisdiction.
      </p>
      <p>
        <strong>We'd rather not sue anyone over a pancake.</strong> Before starting any legal action, please email <a href="mailto:hello@thejoyofpancakes.com">hello@thejoyofpancakes.com</a> so we can try to work it out.
      </p>

      <h2>Changes to these Terms</h2>
      <p>
        If we change these Terms materially, we'll update this page and note the "Last updated" date. Continued use of the service after changes means you accept the new Terms.
      </p>

      <h2>Contact</h2>
      <p>
        <strong>Lucky Bean LLC</strong><br/>
        P.O. Box 63093<br/>
        St. Louis, MO 63163<br/>
        Email: <a href="mailto:hello@thejoyofpancakes.com">hello@thejoyofpancakes.com</a>
      </p>
    </PolicyPage>
  );
}
